import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {Injectable, OnDestroy} from "@angular/core";
import {hypecastHttpContext} from "@app/core/http/http-context";
import {environment} from "@env/environment";
import {Observable, Subject, Subscriber} from "rxjs";
import {catchError, map, shareReplay, takeUntil} from "rxjs/operators";

import {__} from "../functions/object.functions";
import {Tenant} from "../models/classes/Tenant";
import {ITenantService} from "./itenant.service";
import {Response} from "./local/Response";

const tenant_key = "hypecast-tenant-2346cciv3gzw3o51yqn";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

@Injectable()
export class TenantService implements ITenantService, OnDestroy {
  tenant$: Observable<Tenant>;
  tenant!: Tenant;

  private _tenant$: Subject<Tenant> = new Subject<Tenant>();
  private destroySubject?: Subject<void>;

  private get destroy$(): Subject<void> {
    return this.destroySubject ??= new Subject<void>();
  }

  constructor(protected httpClient: HttpClient) {
    this.tenant$ = new Observable<Tenant>((subscriber: Subscriber<Tenant>) => {
      if (!__.IsNullOrUndefined(this.tenant)) {
        subscriber.next(this.tenant);
        subscriber.complete();
        return;
      }

      const subdomain = this.getTenantName();
      let cached: { tenant: Tenant; subdomain: string; expiresAt: string } | null = null;

      if (this.canUseLocalStorage()) {
        const cachedValue = localStorage.getItem(tenant_key);
        cached = cachedValue
          ? JSON.parse(cachedValue) as {
              tenant: Tenant;
              subdomain: string;
              expiresAt: string;
            }
          : null;
      }

      const isCacheValid =
        !__.IsNullOrUndefined(cached) &&
        cached.subdomain === subdomain &&
        !__.IsNullOrUndefined(cached.expiresAt) &&
        new Date(cached.expiresAt).getTime() > Date.now();

      const hasStaleCacheForSubdomain =
        !__.IsNullOrUndefined(cached) && cached.subdomain === subdomain && !isCacheValid;

      if (environment.environmentName === "DEV") {
        const localTenant = Object.assign(new Tenant(), {
          tenantName: environment.subdomain,
          apiUrl: environment.serverUrl,
          cdnUrl: environment.serverUrlCDN,
          clientId: environment.clientId,
          companyName: "Local tenant",
          logoUrl: "https://i0.wp.com/hypecast.one/wp-content/uploads/2021/06/white-1.png?fit=582%2C135&ssl=1",
        } as Tenant);

        this.tenant = localTenant;
        subscriber.next(localTenant);
        subscriber.complete();
        return;
      }

      if (cached && hasStaleCacheForSubdomain) {
        this.tenant = cached.tenant;
        this._tenant$.next(cached.tenant);
        subscriber.next(cached.tenant);
        subscriber.complete();
        this.refreshTenantInBackground(subdomain);
        return;
      }

      if (cached && isCacheValid) {
        this._tenant$.next(cached.tenant);
        this.tenant = cached.tenant;
        subscriber.next(this.tenant);
        subscriber.complete();
        return;
      }

      this.fetchTenant(subdomain)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (fetchedTenant: Tenant) => {
            this.tenant = fetchedTenant;
            subscriber.next(this.tenant);
            subscriber.complete();
          },
          error: (error: unknown) => subscriber.error(error),
        });
    }).pipe(shareReplay({ bufferSize: 1, refCount: false }));
  }

  ngOnDestroy(): void {
    this.destroySubject?.next();
    this.destroySubject?.complete();
  }

  private canUseLocalStorage(): boolean {
    const href = window.location.href;
    return (
      localStorage !== undefined &&
      (!href.includes("coyo-plugin") &&
        !href.includes("staffbase-plugin") &&
        !href.includes("iframe-plugin") &&
        !href.includes("unily-plugin"))
    );
  }

  private fetchTenant(subdomain: string): Observable<Tenant> {
    return this.httpClient.get<Response<Tenant>>(
      `${environment.masterServerUrl}tenants/${subdomain}`,
      {
        context: hypecastHttpContext({
          disableApiPrefix: true,
          disableAccessToken: true,
          disableAuthorization: true,
        }),
      },
    ).pipe(
      map((response: Response<Tenant>) => {
        if (!response.data) {
          throw new Error(`No tenant configuration found for subdomain "${subdomain}".`);
        }

        if (this.canUseLocalStorage()) {
          localStorage.setItem(
            tenant_key,
            JSON.stringify({
              tenant: response.data,
              subdomain,
              expiresAt: new Date(Date.now() + CACHE_TTL_MS).toISOString(),
            })
          );
        }
        this.tenant = response.data;
        this._tenant$.next(response.data);
        return response.data;
      }),
      catchError((error: HttpErrorResponse) => {
        throw error;
      })
    );
  }

  private refreshTenantInBackground(subdomain: string): void {
    this.fetchTenant(subdomain)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (freshTenant: Tenant) => {
          this.tenant = freshTenant;
          this._tenant$.next(freshTenant);
        },
      });
  }

  getTenant$(): Observable<Tenant> {
    return this.tenant$;
  }

  getTenant(): Tenant {
    return this.tenant;
  }

  getTenantName(): string {
    if (environment.environmentName === "DEV" && !__.IsNullOrUndefined(environment.subdomain)) {
      return environment.subdomain;
    }

    const domain = window.location.hostname;
    if (
      domain.indexOf(".") < 0 ||
      domain.split(".")[0] === "example" ||
      domain.split(".")[0] === "lvh" ||
      domain.split(".")[0] === "www"
    ) {
      return "";
    }
    return domain.split(".")[0];
  }

  resetTenant(): void {
    sessionStorage.removeItem(tenant_key);
    localStorage.removeItem(tenant_key);
  }
}
